import React, { useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import '../../assets/styles/Navbar.css';
import logo from '../../assets/optimized/logo-96.png';
import uzFlag from '../../assets/flags/uz.svg';
import krFlag from '../../assets/flags/kr.svg';
import gbFlag from '../../assets/flags/gb.svg';
import ruFlag from '../../assets/flags/ru.svg';
import { APPEALS_WIDGET_OPEN_EVENT } from '../../constants/appealsWidget';
import { useLanguage } from '../../context/LanguageContext';
import { getSiteMessages } from '../../locales/site';
import AppLink from '../common/AppLink';
import { useMenuTree } from '../../hooks/useMenuTree';
import {
  getMenuNodeHref,
  getMenuNodeTitle,
  sortMenuNodes
} from '../../utils/menu';

const topbarSocials = [
  {
    label: 'Telegram',
    href: 'https://t.me/kiuf_uz',
    icon: TelegramIcon
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/kiuf_rasmiy/',
    icon: InstagramIcon
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/koreainternationaluniversityinferghana',
    icon: FacebookIcon
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@kiufuz',
    icon: YoutubeIcon
  }
];

const DESKTOP_NAVIGATION_MEDIA_QUERY = '(min-width: 1081px)';

function normalizeMenuLabel(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/o‘|o'|o`|oʻ/g, 'o')
    .replace(/g‘|g'|g`|gʻ/g, 'g')
    .replace(/’|ʻ|`/g, '')
    .trim();
}

function includesAny(value, patterns = []) {
  return patterns.some((pattern) => value.includes(pattern));
}

function getDropdownIcon(label, variant = 'link') {
  const normalized = normalizeMenuLabel(label);

  if (variant === 'link') {
    if (
      includesAny(normalized, [
        'hujjat',
        'document',
        'doc',
        'fayl',
        'документ',
        'file'
      ])
    ) {
      return ArrowDocumentIcon;
    }

    if (
      includesAny(normalized, [
        'boglan',
        'aloqa',
        'contact',
        'connect',
        'mail',
        'telefon',
        'связ',
        'контакт'
      ])
    ) {
      return ArrowMailIcon;
    }

    return ArrowMenuIcon;
  }

  if (
    includesAny(normalized, [
      'talaba',
      'student',
      'студент',
      'yoshlar'
    ])
  ) {
    return StudentsMenuIcon;
  }

  if (
    includesAny(normalized, [
      'abitur',
      'admission',
      'applicant',
      'qabul',
      'прием'
    ])
  ) {
    return AdmissionMenuIcon;
  }

  if (
    includesAny(normalized, [
      'hujjat',
      'document',
      'doc',
      'fayl',
      'документ',
      'file'
    ])
  ) {
    return DocumentMenuIcon;
  }

  if (
    includesAny(normalized, [
      'boglan',
      'aloqa',
      'contact',
      'connect',
      'mail',
      'telefon',
      'связ',
      'контакт'
    ])
  ) {
    return ContactMenuIcon;
  }

  if (
    includesAny(normalized, [
      'tuzilma',
      'structure',
      'otm',
      'fakultet',
      'kafedra',
      'bolim',
      'section',
      'department',
      'faculty',
      'структур',
      'кафед'
    ])
  ) {
    return StructureMenuIcon;
  }

  if (
    includesAny(normalized, [
      'faoliyat',
      'activity',
      'service',
      'xizmat',
      'марказ',
      'центр'
    ])
  ) {
    return ActivityMenuIcon;
  }

  if (
    includesAny(normalized, [
      'axborot',
      'news',
      'yangilik',
      'media',
      'press',
      'journal',
      'jurnal',
      'новост',
      'инфо'
    ])
  ) {
    return NewsMenuIcon;
  }

  if (
    includesAny(normalized, [
      'xalqaro',
      'international',
      'partner',
      'hamkor',
      'global',
      'междун'
    ])
  ) {
    return GlobalMenuIcon;
  }

  if (
    includesAny(normalized, [
      'xodim',
      'employee',
      'staff',
      'teacher',
      'hodim',
      'personnel',
      'сотруд',
      'препод'
    ])
  ) {
    return StaffMenuIcon;
  }

  if (
    includesAny(normalized, [
      'kelajak',
      'career',
      'road',
      'future',
      'qadam',
      'path',
      'perspective',
      'будущ'
    ])
  ) {
    return DirectionMenuIcon;
  }

  if (
    includesAny(normalized, [
      'gallery',
      'galereya',
      'foto',
      'photo',
      'video'
    ])
  ) {
    return GalleryMenuIcon;
  }

  return variant === 'title' ? FolderIcon : MenuItemIcon;
}

function Navbar() {
  const location = useLocation();
  const headerRef = useRef(null);
  const navbarRowRef = useRef(null);
  const navMenuRef = useRef(null);

  const { menus: navItems, loading } = useMenuTree();
  const {
    currentLanguage,
    currentLanguageCode,
    languageOptions,
    setLanguage
  } = useLanguage();

  const texts = React.useMemo(
    () => getSiteMessages(currentLanguageCode).navbar,
    [currentLanguageCode]
  );

  const [open, setOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [isDesktopNavigation, setIsDesktopNavigation] = useState(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return false;
    }

    return window.matchMedia(DESKTOP_NAVIGATION_MEDIA_QUERY).matches;
  });

  const isAcademicDetailPage = Boolean(
    matchPath({ path: '/faculties/:id', end: false }, location.pathname) ||
      matchPath(
        { path: '/departments/:facultyId/:departmentId', end: false },
        location.pathname
      ) ||
      matchPath({ path: '/sections/:id', end: false }, location.pathname)
  );

  const topbarInfo = React.useMemo(
    () => [
      {
        label: texts.topbarInfo.location,
        href: '#aloqa',
        icon: MapPinIcon
      },
      {
        label: texts.topbarInfo.phone,
        href: `tel:${String(texts.topbarInfo.phone || '')
          .split(',')[0]
          .replace(/\s+/g, '')}`,
        icon: PhoneIcon
      },
      {
        label: texts.topbarInfo.email,
        href: `mailto:${texts.topbarInfo.email}`,
        icon: MailIcon
      }
    ],
    [texts]
  );

  const topbarLinks = React.useMemo(
    () => [
      {
        label: texts.topbarLinks.hemis,
        href: '#',
        icon: TeamIcon
      },
      {
        label: texts.topbarLinks.lms,
        href: '#',
        icon: StudentIcon
      },
      {
        label: texts.topbarLinks.corporateEmail,
        href: '#',
        icon: MailIcon
      },
      {
        label: texts.topbarLinks.eLibrary,
        href: 'https://elib.ukiu.uz/',
        icon: DocumentMenuIcon
      }
    ],
    [texts]
  );

  useEffect(() => {
    setOpen(false);
    setOpenSubmenu(null);
    setLanguageOpen(false);
  }, [location.pathname, location.hash]);

  /*
   * Navbar real sig'ishini tekshiradi.
   * Logo + menu + Aloqa xavfsiz sig'masa hamburger rejimiga o'tadi.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let frameId;
    let resizeObserver;
    const NAVBAR_SAFETY_GAP = 48;

    const checkNavbarFit = () => {
      window.cancelAnimationFrame(frameId);

      frameId = window.requestAnimationFrame(() => {
        const row = navbarRowRef.current;
        const menu = navMenuRef.current;
        const brand = row?.querySelector('.brand');
        const actions = row?.querySelector('.navbar-actions');

        if (!row || !menu || !brand || !actions) {
          return;
        }

        const forceMobile = window.innerWidth <= 1080;

        /*
         * Collapsed holatda nav yashirin.
         * navbar-measuring class orqali menu natural kenglikda o'lchanadi.
         */
        row.classList.add('navbar-measuring');

        const rowStyles = window.getComputedStyle(row);
        const gap =
          Number.parseFloat(
            rowStyles.columnGap || rowStyles.gap || '0'
          ) || 0;

        const brandWidth = Math.max(
          300,
          Math.ceil(brand.getBoundingClientRect().width),
          brand.scrollWidth
        );

        const menuWidth = Math.max(
          Math.ceil(menu.getBoundingClientRect().width),
          menu.scrollWidth
        );

        const actionsWidth = Math.max(
          Math.ceil(actions.getBoundingClientRect().width),
          actions.scrollWidth
        );

        const requiredWidth =
          brandWidth +
          menuWidth +
          actionsWidth +
          gap * 2 +
          NAVBAR_SAFETY_GAP;

        const availableWidth = row.clientWidth;

        row.classList.remove('navbar-measuring');

        const shouldCollapse =
          forceMobile || requiredWidth >= availableWidth;

        setIsNavCollapsed((previous) =>
          previous === shouldCollapse ? previous : shouldCollapse
        );

        setIsDesktopNavigation(!shouldCollapse);

        if (!shouldCollapse) {
          setOpen(false);
          setOpenSubmenu(null);
          document.body.classList.remove(
            'kiuf-mobile-menu-open'
          );
        }
      });
    };

    checkNavbarFit();

    window.addEventListener('resize', checkNavbarFit);
    window.addEventListener(
      'orientationchange',
      checkNavbarFit
    );

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkNavbarFit);

      if (navbarRowRef.current) {
        resizeObserver.observe(navbarRowRef.current);
      }

      if (navMenuRef.current) {
        resizeObserver.observe(navMenuRef.current);
      }
    }

    return () => {
      window.cancelAnimationFrame(frameId);

      window.removeEventListener(
        'resize',
        checkNavbarFit
      );

      window.removeEventListener(
        'orientationchange',
        checkNavbarFit
      );

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [currentLanguageCode, navItems, texts.contact]);

  /*
   * Desktop/mobile holat real navbar sig'ishiga qarab yuqoridagi
   * checkNavbarFit orqali boshqariladi.
   */

  /*
   * Mobil menyu fixed bo'lgani uchun headerning real
   * pastki koordinatasini CSS variable'ga beramiz.
   * Shunda menyu header tagiga kirib ketmaydi.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateMobileMenuTop = () => {
      if (!headerRef.current) {
        return;
      }

      const rect = headerRef.current.getBoundingClientRect();
      const bottom = Math.max(0, Math.round(rect.bottom));

      document.documentElement.style.setProperty(
        '--kiuf-mobile-menu-top',
        `${bottom}px`
      );
    };

    updateMobileMenuTop();

    const frameId = window.requestAnimationFrame(
      updateMobileMenuTop
    );

    window.addEventListener('resize', updateMobileMenuTop);
    window.addEventListener(
      'orientationchange',
      updateMobileMenuTop
    );
    window.addEventListener('scroll', updateMobileMenuTop, {
      passive: true
    });

    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined' && headerRef.current) {
      resizeObserver = new ResizeObserver(updateMobileMenuTop);
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateMobileMenuTop);
      window.removeEventListener(
        'orientationchange',
        updateMobileMenuTop
      );
      window.removeEventListener('scroll', updateMobileMenuTop);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [open, languageOpen, currentLanguageCode, navItems.length]);

  /*
   * Mobil menyu ochilganda orqa sahifa scroll bo'lmaydi.
   * Faqat menyuning o'zi scroll bo'ladi.
   */
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (open && !isDesktopNavigation) {
      document.body.classList.add('kiuf-mobile-menu-open');
    } else {
      document.body.classList.remove('kiuf-mobile-menu-open');
    }

    return () => {
      document.body.classList.remove('kiuf-mobile-menu-open');
    };
  }, [open, isDesktopNavigation]);

  const toggleMenu = () => {
    setOpen((prevOpen) => {
      const nextOpen = !prevOpen;

      if (!nextOpen) {
        setOpenSubmenu(null);
      }

      return nextOpen;
    });
  };

  const toggleSubmenu = (id) => {
    setOpenSubmenu((currentId) =>
      currentId === id ? null : id
    );
  };

  const handleDesktopNavEnter = (itemId, hasChildren) => {
    if (!isDesktopNavigation) {
      return;
    }

    setOpenSubmenu(hasChildren ? itemId : null);
  };

  const handleDesktopNavLeave = () => {
    if (!isDesktopNavigation) {
      return;
    }

    setOpenSubmenu(null);
  };

  const handleNavClick = () => {
    setOpen(false);
    setOpenSubmenu(null);
    setLanguageOpen(false);
  };

  const handleContactOpen = () => {
    handleNavClick();

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(APPEALS_WIDGET_OPEN_EVENT)
      );
    }
  };

  const handleLanguageSelect = (language) => {
    setLanguage(language);
    setLanguageOpen(false);
  };

  const normalizeLanguageCode = (code = '') => {
    return String(code || '').trim().toLowerCase();
  };

  const languageFlagMap = React.useMemo(
    () => ({
      uz: uzFlag,
      ko: krFlag,
      kr: krFlag,
      en: gbFlag,
      gb: gbFlag,
      ru: ruFlag
    }),
    []
  );

  const getLanguageFlag = (code = '') => {
    const normalizedCode = normalizeLanguageCode(code);

    return languageFlagMap[normalizedCode] || null;
  };

  const orderedLanguages = React.useMemo(() => {
    const order = ['uz', 'ko', 'kr', 'en', 'gb', 'ru'];

    return [...languageOptions].sort((a, b) => {
      const aCode = normalizeLanguageCode(a.code);
      const bCode = normalizeLanguageCode(b.code);

      const aIndex = order.indexOf(aCode);
      const bIndex = order.indexOf(bCode);

      return (
        (aIndex === -1 ? 99 : aIndex) -
        (bIndex === -1 ? 99 : bIndex)
      );
    });
  }, [languageOptions]);

  return (
    <header
      ref={headerRef}
      className={`kiuf-header${
        isAcademicDetailPage ? ' kiuf-header--overlay' : ''
      }`}
    >
      <div className="kiuf-topbar">
        <div className="nav-shell kiuf-inner topbar-row">
          <div className="topbar-left">
            {topbarInfo.map(
              ({ label, href, icon: Icon }, index) => (
                <React.Fragment key={`${label}-${index}`}>
                  <AppLink
                    href={href}
                    className="topbar-entry"
                  >
                    <Icon className="topbar-icon" />
                    <span>{label}</span>
                  </AppLink>

                  {index < topbarInfo.length - 1 && (
                    <span
                      className="topbar-pipe"
                      aria-hidden="true"
                    >
                      |
                    </span>
                  )}
                </React.Fragment>
              )
            )}
          </div>

          <div className="topbar-right">
            {topbarLinks.map(
              ({ label, href, icon: Icon }, index) => (
                <React.Fragment key={`${label}-${index}`}>
                  <AppLink
                    href={href}
                    className="topbar-entry topbar-link"
                  >
                    <Icon className="topbar-icon" />
                    <span>{label}</span>
                  </AppLink>

                  {index < topbarLinks.length - 1 && (
                    <span
                      className="topbar-pipe"
                      aria-hidden="true"
                    >
                      |
                    </span>
                  )}
                </React.Fragment>
              )
            )}

            <span
              className="topbar-pipe"
              aria-hidden="true"
            >
              |
            </span>

            <div className="topbar-socials">
              {topbarSocials.map(
                ({ label, href, icon: Icon }) => (
                  <AppLink
                    key={label}
                    href={href}
                    className="topbar-social"
                    aria-label={label}
                  >
                    <Icon className="topbar-social-icon" />
                  </AppLink>
                )
              )}
            </div>

            <span
              className="topbar-pipe"
              aria-hidden="true"
            >
              |
            </span>

            <div
              className={`language-dropdown ${
                languageOpen ? 'active' : ''
              }`}
            >
              <button
                className="lang-btn"
                type="button"
                onClick={() =>
                  setLanguageOpen((prev) => !prev)
                }
                aria-expanded={languageOpen}
                aria-label={
                  currentLanguage.name ||
                  currentLanguage.label
                }
              >
                <span
                  className="language-flag"
                  aria-hidden="true"
                >
                  {getLanguageFlag(currentLanguage.code) ? (
                    <img
                      src={getLanguageFlag(
                        currentLanguage.code
                      )}
                      alt=""
                      className="language-flag-image"
                    />
                  ) : (
                    <span className="language-flag-fallback">
                      🌐
                    </span>
                  )}
                </span>

                <ChevronDownIcon className="topbar-caret" />
              </button>

              {languageOpen && (
                <div className="language-menu">
                  {orderedLanguages.map((language) => (
                    <button
                      key={language.code}
                      type="button"
                      className={`language-item ${
                        normalizeLanguageCode(
                          currentLanguage.code
                        ) ===
                        normalizeLanguageCode(
                          language.code
                        )
                          ? 'active'
                          : ''
                      }`}
                      onClick={() =>
                        handleLanguageSelect(language)
                      }
                    >
                      <span
                        className="language-flag"
                        aria-hidden="true"
                      >
                        {getLanguageFlag(language.code) ? (
                          <img
                            src={getLanguageFlag(
                              language.code
                            )}
                            alt=""
                            className="language-flag-image"
                          />
                        ) : (
                          <span className="language-flag-fallback">
                            🌐
                          </span>
                        )}
                      </span>

                      <span className="language-name">
                        {language.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="kiuf-navbar">
        <div
          ref={navbarRowRef}
          className={`nav-shell kiuf-inner navbar-row ${
            isNavCollapsed ? 'navbar-collapsed' : ''
          }`}
        >
          <AppLink href="/" className="brand">
            <img
              src={logo}
              alt="KIUF Logo"
              className="brand-logo"
              width="96"
              height="96"
              fetchPriority="high"
              decoding="async"
            />

            <div className="brand-text">
              <div className="brand-top">
                <span className="brand-main">KIUF</span>
                <span className="brand-sub">
                  {texts.brand.sub}
                </span>
              </div>

              <div className="brand-korean">
                {texts.brand.korean}
              </div>
            </div>
          </AppLink>

          <nav
            ref={navMenuRef}
            className={`nav-menu ${
              open ? 'active' : ''
            }`}
            onMouseLeave={handleDesktopNavLeave}
          >
            {!navItems.length && loading ? (
              <div
                className="nav-loading"
                aria-hidden="true"
              >
                <span className="nav-loading-pill" />
                <span className="nav-loading-pill" />
                <span className="nav-loading-pill" />
              </div>
            ) : (
              navItems.map((item) => {
                const hasChildren = Boolean(
                  item.children?.length
                );
                const isSubmenuOpen =
                  openSubmenu === item.id;

                const title = getMenuNodeTitle(
                  item,
                  currentLanguageCode
                );

                return (
                  <div
                    key={item.id}
                    className={`nav-item ${
                      hasChildren ? 'has-dropdown' : ''
                    } ${
                      isSubmenuOpen
                        ? 'submenu-open'
                        : ''
                    }`}
                    onMouseEnter={() =>
                      handleDesktopNavEnter(
                        item.id,
                        hasChildren
                      )
                    }
                  >
                    <div className="nav-link-group">
                      <AppLink
                        href={getMenuNodeHref(item)}
                        className="nav-link"
                        onClick={handleNavClick}
                      >
                        <span>{title}</span>
                      </AppLink>

                      {hasChildren && (
                        <button
                          type="button"
                          className="nav-caret-btn"
                          aria-label={`${title} ${texts.openSectionSuffix}`}
                          aria-expanded={
                            isSubmenuOpen
                          }
                          onClick={() =>
                            toggleSubmenu(item.id)
                          }
                        >
                          <ChevronDownIcon className="nav-caret-icon" />
                        </button>
                      )}
                    </div>

                    {hasChildren && (
                      <div className="dropdown-menu mega-menu">
                        <MegaDropdown
                          items={item.children}
                          languageCode={
                            currentLanguageCode
                          }
                          onNavigate={
                            handleNavClick
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </nav>

          <div className="navbar-actions">
            <button
              type="button"
              className="navbar-contact-btn"
              onClick={handleContactOpen}
              aria-label={texts.openContact}
            >
              {texts.contact}
            </button>

            <button
              type="button"
              className="mobile-btn"
              onClick={toggleMenu}
              aria-label={texts.mobileMenu}
              aria-expanded={open}
            >
              {open ? '✕' : '☰'}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function MegaDropdown({
  items,
  languageCode,
  onNavigate
}) {
  const columns = sortMenuNodes(items);
  const [openColumn, setOpenColumn] = useState(null);

  const toggleColumn = (id) => {
    setOpenColumn((current) =>
      current === id ? null : id
    );
  };

  return (
    <div className="mega-menu-inner">
      {columns.map((column) => {
        const title = getMenuNodeTitle(
          column,
          languageCode
        );

        const TitleIcon = getDropdownIcon(
          title,
          'title'
        );

        const children = sortMenuNodes(
          column.children || []
        );

        const hasChildren = children.length > 0;
        const isOpen = openColumn === column.id;

        return (
          <div
            key={column.id}
            className={`mega-column ${
              isOpen ? 'mega-column-open' : ''
            }`}
          >
            <div className="mega-title-row">
              <AppLink
                href={getMenuNodeHref(column)}
                className="mega-title"
                onClick={onNavigate}
              >
                <span className="mega-title-icon-wrap">
                  <TitleIcon className="mega-title-icon" />
                </span>

                <span className="mega-title-text">
                  {title}
                </span>
              </AppLink>

              {hasChildren && (
                <button
                  type="button"
                  className="mega-title-toggle"
                  aria-expanded={isOpen}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleColumn(column.id);
                  }}
                >
                  <ChevronDownIcon className="mega-title-caret" />
                </button>
              )}
            </div>

            {hasChildren ? (
              <div className="mega-links">
                {children.map((child) => (
                  <MegaDropdownLink
                    key={child.id}
                    item={child}
                    languageCode={languageCode}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            ) : (
              <div className="mega-links mega-links-single">
                <AppLink
                  href={getMenuNodeHref(column)}
                  className="mega-link"
                  onClick={onNavigate}
                >
                  <span className="mega-link-icon-wrap">
                    <TitleIcon className="mega-link-icon" />
                  </span>

                  <span className="mega-link-text">
                    Batafsil
                  </span>
                </AppLink>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MegaDropdownLink({
  item,
  languageCode,
  onNavigate
}) {
  const [open, setOpen] = useState(false);

  const title = getMenuNodeTitle(
    item,
    languageCode
  );

  const LinkIcon = getDropdownIcon(
    title,
    'link'
  );

  const children = sortMenuNodes(
    item.children || []
  );

  const hasChildren = children.length > 0;

  return (
    <div
      className={`mega-link-group ${
        open ? 'mega-link-group-open' : ''
      }`}
    >
      <div className="mega-link-row">
        <AppLink
          href={getMenuNodeHref(item)}
          className="mega-link"
          onClick={onNavigate}
        >
          <span className="mega-link-icon-wrap">
            <LinkIcon className="mega-link-icon" />
          </span>

          <span className="mega-link-text">
            {title}
          </span>
        </AppLink>

        {hasChildren && (
          <button
            type="button"
            className="mega-link-toggle"
            aria-expanded={open}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setOpen((prev) => !prev);
            }}
          >
            <ChevronDownIcon className="mega-link-caret" />
          </button>
        )}
      </div>

      {hasChildren && (
        <div className="mega-sub-links">
          {children.map((child) => (
            <NestedMegaLink
              key={child.id}
              item={child}
              languageCode={languageCode}
              onNavigate={onNavigate}
              level={1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NestedMegaLink({
  item,
  languageCode,
  onNavigate,
  level = 1
}) {
  const [open, setOpen] = useState(false);

  const title = getMenuNodeTitle(
    item,
    languageCode
  );

  const children = sortMenuNodes(
    item.children || []
  );

  const hasChildren = children.length > 0;

  return (
    <div
      className={`mega-nested-group ${
        open ? 'mega-nested-open' : ''
      }`}
      style={{
        '--nested-level': level
      }}
    >
      <div className="mega-sub-row">
        <AppLink
          href={getMenuNodeHref(item)}
          className="mega-sub-link"
          onClick={onNavigate}
        >
          <span className="mega-sub-dot" />

          <span className="mega-sub-text">
            {title}
          </span>
        </AppLink>

        {hasChildren && (
          <button
            type="button"
            className="mega-sub-toggle"
            aria-expanded={open}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setOpen((prev) => !prev);
            }}
          >
            <ChevronDownIcon className="mega-sub-caret" />
          </button>
        )}
      </div>

      {hasChildren && (
        <div className="mega-nested-links">
          {children.map((child) => (
            <NestedMegaLink
              key={child.id}
              item={child}
              languageCode={languageCode}
              onNavigate={onNavigate}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M4 7.5h16v11H4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M4 10.5h16M9 7.5V5h6v2.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MenuItemIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M7 6.5h10v11H7z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M10 10h4m-4 3.5h4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ArrowMenuIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M6 12h10"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
      <path
        d="m12 7 5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowDocumentIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M5 7h8M5 12h8M5 17h8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M13 8.5h5M15 6.5l3 2-3 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowMailIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M4.5 7.5h9M4.5 12h9M14 12h5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="m16 9 3 3-3 3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StudentsMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="m3 9 9-4.5L21 9l-9 4.5L3 9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M7 11.5v3.2c0 1.4 2.2 2.8 5 2.8s5-1.4 5-2.8v-3.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 10.8v4.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function AdmissionMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M16.5 4.5v3M7.5 4.5v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function DocumentMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M7 3.5h7l4 4v13H7a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M14 3.5v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 11h6M9 14.5h6M9 18h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function ContactMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 6.5h14v9H8l-3 3v-12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 10h6M9 13.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function StructureMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="4" y="5" width="5" height="5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="15" y="5" width="5" height="5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="9.5" y="14" width="5" height="5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M9 7.5h6M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function ActivityMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M4 18h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="m6.5 14.5 3-3 3 2 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17.5 8.5H14v3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NewsMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 6h14v12H5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M8 9h4M8 12.5h8M8 16h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <rect x="13.5" y="8.5" width="3.5" height="2.5" fill="currentColor" />
    </svg>
  );
}

function DirectionMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="m10 10 5-2-2 5-4 2 1-5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function GlobalMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 12h17M12 3.5c2.2 2.3 3.4 5.3 3.4 8.5s-1.2 6.2-3.4 8.5c-2.2-2.3-3.4-5.3-3.4-8.5S9.8 5.8 12 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function StaffMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="16.5" cy="9.5" r="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 19c.7-2.3 2.7-3.8 5.1-3.8h1c2.4 0 4.4 1.5 5.1 3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14 19c.5-1.5 1.8-2.5 3.4-2.5h.5c1.1 0 2.2.5 2.9 1.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function GalleryMenuIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m7 15 3-3 2.5 2.2 2-1.7L17 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="10" r="1.2" fill="currentColor" />
    </svg>
  );
}

function MapPinIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 21s6-5.13 6-11a6 6 0 1 0-12 0c0 5.87 6 11 6 11Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.2" fill="currentColor" />
    </svg>
  );
}

function PhoneIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M21 16.42v2.75a1.83 1.83 0 0 1-2 1.83A18.2 18.2 0 0 1 3 5a1.83 1.83 0 0 1 1.82-2H7.6a1.83 1.83 0 0 1 1.78 1.45l.58 2.62a1.83 1.83 0 0 1-.52 1.72l-1.2 1.2a14.65 14.65 0 0 0 5.77 5.77l1.2-1.2a1.83 1.83 0 0 1 1.72-.52l2.62.58A1.83 1.83 0 0 1 21 16.42Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MailIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m22 8-8.97 5.7a2 2 0 0 1-2.06 0L2 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StudentIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="m2 9 10-5 10 5-10 5L2 9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M6 11.5V16c0 1.66 2.69 3 6 3s6-1.34 6-3v-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TeamIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDownIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TelegramIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M21.5 4.5 3.3 11.5c-1.2.5-1.2 1.2-.2 1.5l4.7 1.5 1.8 5.5c.2.7.4 1 1 1 .5 0 .8-.2 1.2-.6l2.6-2.5 5.3 3.9c1 .5 1.7.3 2-1l3.2-15.1c.3-1.2-.5-1.7-1.4-1.3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m8 14.5 10.7-6.8-8.3 8.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InstagramIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.3" cy="6.7" r="1.1" fill="currentColor" />
    </svg>
  );
}

function FacebookIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M14 8h2V4h-2.8C10.4 4 9 5.7 9 8.3V11H6v4h3v6h4v-6h3l.6-4H13V8.8c0-.6.3-.8 1-.8Z" fill="currentColor" />
    </svg>
  );
}

function YoutubeIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M22 12s0-3.4-.4-5a2.8 2.8 0 0 0-2-2C17.8 4.5 12 4.5 12 4.5s-5.8 0-7.6.5a2.8 2.8 0 0 0-2 2C2 8.6 2 12 2 12s0 3.4.4 5a2.8 2.8 0 0 0 2 2c1.8.5 7.6.5 7.6.5s5.8 0 7.6-.5a2.8 2.8 0 0 0 2-2c.4-1.6.4-5 .4-5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m10 9 5 3-5 3V9Z" fill="currentColor" />
    </svg>
  );
}

export default Navbar;
